import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';
import {Node, XYPosition} from 'reactflow';

import {BenchlingNode} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNode';

import {
  ProcessFlowchartNodeSection,
  ProcessFlowchartNodeType,
} from '../processFlowchartConfigEditorConstants';
import {NODE_TYPE_TO_NAME, NODE_TYPE_TO_SECTION} from '../processFlowchartConfigEditorRegistry';
import {FlowchartOptions} from '../processFlowchartConfigEditorUtils';
import {getLogicNodeCreationContext, getNewLogicNode} from './logicNodeCreationUtils';
import {NodeSidepanelSectionProps} from './NodeSidepanelSection';
import {DraggableNode} from './sidepanelNodeUtils';

function DraggableLogicNode(props: {
  nodeType: ProcessFlowchartNodeType;
  addNodeToCanvas: (node: Node<unknown>) => void;
  options: FlowchartOptions;
  reactFlowWrapper: React.RefObject<HTMLDivElement>;
}): JSX.Element {
  const staticNodeData = useMemo(() => getLogicNodeCreationContext(props.nodeType), [props.nodeType]);

  const addNode = useCallback(
    (position: XYPosition) => {
      const newNode = getNewLogicNode(staticNodeData, position, props.options);
      props.addNodeToCanvas(newNode);
    },
    [props, staticNodeData]
  );

  const nodeRenderer = useCallback(
    ({dragging}: {dragging: boolean}) => (
      <BenchlingNode.Standard.Node
        headerProps={{
          title: staticNodeData.nodeName,
          leftIcon: staticNodeData.nodeIcon,
        }}
        width="100%"
      />
    ),
    [staticNodeData.nodeName, staticNodeData.nodeIcon]
  );

  return (
    <DraggableNode
      addNode={addNode}
      nodeRenderer={nodeRenderer}
      reactFlowWrapper={props.reactFlowWrapper}
      styleOverrides={[styles.noMargin]}
      dataTestAttrs={{
        component: 'LogicNodeSidepanelSection',
        element: 'DraggableLogicNode',
        key: props.nodeType,
      }}
    />
  );
}

/**
 * A sidepanel component that displays all available task nodes that can be added to the flowchart
 */
export default function LogicNodeSidepanelSection(
  props: Omit<NodeSidepanelSectionProps, 'section'>
): JSX.Element {
  const logicNodeTypes = props.nodeTypes.filter(
    (nodeType) => NODE_TYPE_TO_SECTION[nodeType] === ProcessFlowchartNodeSection.LOGIC
  );
  return (
    <div className={css(styles.logicNodesContainer)}>
      {logicNodeTypes
        .filter((nodeType) => {
          if (props.searchQuery == null) {
            return true;
          }
          return NODE_TYPE_TO_NAME[nodeType].toLowerCase().includes(props.searchQuery.toLowerCase());
        })
        .map((nodeType) => (
          <DraggableLogicNode
            key={nodeType}
            nodeType={nodeType}
            addNodeToCanvas={props.addNodeToCanvas}
            options={props.options}
            reactFlowWrapper={props.reactFlowWrapper}
          />
        ))}
    </div>
  );
}

const styles = StyleSheet.create({
  logicNodesContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    width: '100%',
    gap: 12,
  },
  noMargin: {
    margin: 0,
  },
});
